/**
 * PanelLayout - Three-column resizable panel layout
 *
 * BYK-156: Foundation for the new sidebar-based UI
 * Replaces floating BentoPanel modals with fixed sidebars
 *
 * Structure:
 * ┌─────────────┬─────────────────────────┬─────────────┐
 * │ Left        │                         │ Right       │
 * │ Sidebar     │       Center            │ Sidebar     │
 * │ (Create)    │       (Canvas)          │ (Modify)    │
 * └─────────────┴─────────────────────────┴─────────────┘
 */

import { useCallback, useEffect, useRef } from "react";
import {
  Panel,
  Group,
  Separator,
  type PanelImperativeHandle,
} from "react-resizable-panels";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================================================
// Types
// ============================================================================

export interface PanelLayoutProps {
  /** Content for the left sidebar (Create tools) */
  leftSidebar: React.ReactNode;
  /** Content for the center area (Canvas) */
  center: React.ReactNode;
  /** Content for the right sidebar (Modify/Properties) */
  rightSidebar: React.ReactNode;
  /** Header content (optional - for toolbar) */
  header?: React.ReactNode;
  /** Footer content (optional - for status bar) */
  footer?: React.ReactNode;
  /** Callback when any panel resizes (for Babylon.js resize) */
  onPanelResize?: () => void;
  /** Left sidebar collapsed state */
  leftCollapsed?: boolean;
  /** Right sidebar collapsed state */
  rightCollapsed?: boolean;
  /** Callback when left sidebar collapse state changes */
  onLeftCollapsedChange?: (collapsed: boolean) => void;
  /** Callback when right sidebar collapse state changes */
  onRightCollapsedChange?: (collapsed: boolean) => void;
  /** Default left sidebar size (percentage) */
  defaultLeftSize?: number;
  /** Default right sidebar size (percentage) */
  defaultRightSize?: number;
  /** Minimum left sidebar size (percentage) */
  minLeftSize?: number;
  /** Minimum right sidebar size (percentage) */
  minRightSize?: number;
  /** Maximum left sidebar size (percentage) */
  maxLeftSize?: number;
  /** Maximum right sidebar size (percentage) */
  maxRightSize?: number;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_LEFT_SIZE = 18; // 18% of width
const DEFAULT_RIGHT_SIZE = 18;
const MIN_PANEL_SIZE = 10; // 10% minimum
const MAX_PANEL_SIZE = 30; // 30% maximum

// ============================================================================
// Resize Handle Component
// ============================================================================

interface ResizeHandleProps {
  direction: "left" | "right";
  isCollapsed?: boolean;
  onToggle?: () => void;
}

function ResizeHandle({ direction, isCollapsed, onToggle }: ResizeHandleProps) {
  return (
    <Separator
      className={cn(
        "group relative flex w-1.5 items-center justify-center",
        "bg-gray-200/50 transition-colors duration-150",
        "hover:bg-blue-400/50 active:bg-blue-500/60",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      )}
    >
      {/* Visible handle bar */}
      <div
        className={cn(
          "absolute h-8 w-1 rounded-full bg-gray-300",
          "transition-all duration-150",
          "group-hover:h-12 group-hover:bg-blue-400",
          "group-active:bg-blue-500"
        )}
      />

      {/* Collapse toggle button */}
      {onToggle && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className={cn(
            "absolute z-10 flex h-6 w-6 items-center justify-center",
            "rounded-full bg-white shadow-md border border-gray-200",
            "opacity-0 transition-opacity duration-150",
            "group-hover:opacity-100",
            "hover:bg-gray-50 active:bg-gray-100",
            direction === "left" ? "-left-3" : "-right-3"
          )}
          aria-label={isCollapsed ? "Expand panel" : "Collapse panel"}
        >
          {direction === "left" ? (
            isCollapsed ? (
              <ChevronRight className="h-3 w-3 text-gray-600" />
            ) : (
              <ChevronLeft className="h-3 w-3 text-gray-600" />
            )
          ) : isCollapsed ? (
            <ChevronLeft className="h-3 w-3 text-gray-600" />
          ) : (
            <ChevronRight className="h-3 w-3 text-gray-600" />
          )}
        </button>
      )}
    </Separator>
  );
}

// ============================================================================
// Panel Layout Component
// ============================================================================

export function PanelLayout({
  leftSidebar,
  center,
  rightSidebar,
  header,
  footer,
  onPanelResize,
  leftCollapsed = false,
  rightCollapsed = false,
  onLeftCollapsedChange,
  onRightCollapsedChange,
  defaultLeftSize = DEFAULT_LEFT_SIZE,
  defaultRightSize = DEFAULT_RIGHT_SIZE,
  minLeftSize = MIN_PANEL_SIZE,
  minRightSize = MIN_PANEL_SIZE,
  maxLeftSize = MAX_PANEL_SIZE,
  maxRightSize = MAX_PANEL_SIZE,
}: PanelLayoutProps) {
  const leftPanelRef = useRef<PanelImperativeHandle>(null);
  const rightPanelRef = useRef<PanelImperativeHandle>(null);

  // Debounced resize callback for Babylon.js
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();

  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(() => {
      onPanelResize?.();
    }, 50); // 50ms debounce
  }, [onPanelResize]);

  // Handle left panel collapse
  const toggleLeftPanel = useCallback(() => {
    const panel = leftPanelRef.current;
    if (!panel) return;

    if (leftCollapsed) {
      panel.expand();
    } else {
      panel.collapse();
    }
  }, [leftCollapsed, leftPanelRef]);

  // Handle right panel collapse
  const toggleRightPanel = useCallback(() => {
    const panel = rightPanelRef.current;
    if (!panel) return;

    if (rightCollapsed) {
      panel.expand();
    } else {
      panel.collapse();
    }
  }, [rightCollapsed, rightPanelRef]);

  // Track collapsed state via onResize
  const handleLeftResize = useCallback(
    (size: { asPercentage: number }) => {
      const isNowCollapsed = size.asPercentage === 0;
      if (isNowCollapsed !== leftCollapsed) {
        onLeftCollapsedChange?.(isNowCollapsed);
      }
    },
    [leftCollapsed, onLeftCollapsedChange]
  );

  const handleRightResize = useCallback(
    (size: { asPercentage: number }) => {
      const isNowCollapsed = size.asPercentage === 0;
      if (isNowCollapsed !== rightCollapsed) {
        onRightCollapsedChange?.(isNowCollapsed);
      }
    },
    [rightCollapsed, onRightCollapsedChange]
  );

  // Sync external collapsed state with panels
  // Use setTimeout to ensure panel constraints are registered before calling isCollapsed()
  useEffect(() => {
    const leftPanel = leftPanelRef.current;
    if (!leftPanel) return;

    // Delay to ensure panel is fully registered with Group
    const timeoutId = setTimeout(() => {
      try {
        const isCurrentlyCollapsed = leftPanel.isCollapsed();
        if (leftCollapsed && !isCurrentlyCollapsed) {
          leftPanel.collapse();
        } else if (!leftCollapsed && isCurrentlyCollapsed) {
          leftPanel.expand();
        }
      } catch {
        // Panel not yet registered, will sync on next render
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [leftCollapsed]);

  useEffect(() => {
    const rightPanel = rightPanelRef.current;
    if (!rightPanel) return;

    // Delay to ensure panel is fully registered with Group
    const timeoutId = setTimeout(() => {
      try {
        const isCurrentlyCollapsed = rightPanel.isCollapsed();
        if (rightCollapsed && !isCurrentlyCollapsed) {
          rightPanel.collapse();
        } else if (!rightCollapsed && isCurrentlyCollapsed) {
          rightPanel.expand();
        }
      } catch {
        // Panel not yet registered, will sync on next render
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [rightCollapsed]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="flex h-dvh w-screen flex-col bg-gray-100" style={{ overflow: 'clip', position: 'fixed', top: 0, left: 0 }}>
      {/* Header (Toolbar) */}
      {header && (
        <header className="relative z-40 min-h-14 flex-shrink-0 border-b border-gray-200 bg-white">
          {header}
        </header>
      )}

      {/* Main Panel Group */}
      <Group
        orientation="horizontal"
        onLayoutChange={handleResize}
        className="flex-1"
        id="byk-panel-layout"
      >
        {/* Left Sidebar */}
        <Panel
          panelRef={leftPanelRef}
          defaultSize={`${defaultLeftSize}%`}
          minSize={`${minLeftSize}%`}
          maxSize={`${maxLeftSize}%`}
          collapsible
          collapsedSize="0%"
          onResize={handleLeftResize}
          className={cn(
            "bg-white border-r border-gray-200",
            "transition-[flex] duration-200 ease-out"
          )}
          aria-label="Left sidebar - Create tools"
        >
          <div className="h-full overflow-hidden" style={{ overscrollBehaviorY: 'contain' }}>
            {leftSidebar}
          </div>
        </Panel>

        {/* Left Resize Handle */}
        <ResizeHandle
          direction="left"
          isCollapsed={leftCollapsed}
          onToggle={toggleLeftPanel}
        />

        {/* Center (Canvas) */}
        <Panel
          defaultSize={`${100 - defaultLeftSize - defaultRightSize}%`}
          minSize="40%"
          className="bg-gray-50"
          aria-label="Main canvas area"
        >
          <div className="h-full overflow-hidden" style={{ isolation: 'isolate', overscrollBehaviorY: 'contain' }}>
            {center}
          </div>
        </Panel>

        {/* Right Resize Handle */}
        <ResizeHandle
          direction="right"
          isCollapsed={rightCollapsed}
          onToggle={toggleRightPanel}
        />

        {/* Right Sidebar */}
        <Panel
          panelRef={rightPanelRef}
          defaultSize={`${defaultRightSize}%`}
          minSize={`${minRightSize}%`}
          maxSize={`${maxRightSize}%`}
          collapsible
          collapsedSize="0%"
          onResize={handleRightResize}
          className={cn(
            "bg-white border-l border-gray-200",
            "transition-[flex] duration-200 ease-out"
          )}
          aria-label="Right sidebar - Properties and settings"
        >
          <div className="h-full overflow-hidden" style={{ overscrollBehaviorY: 'contain' }}>
            {rightSidebar}
          </div>
        </Panel>
      </Group>

      {/* Footer (Status Bar) */}
      {footer && (
        <div className="z-40 flex-shrink-0 border-t border-gray-200 bg-white/95 backdrop-blur-sm">
          {footer}
        </div>
      )}
    </div>
  );
}

export default PanelLayout;
